import React from 'react';
import s from './Header.scss';
import {useControllerProps} from '../../ControllerContext';
import settingsParams from '../../../settingsParams';
import stylesParams from '../../../stylesParams';
import {useStyles} from '@wix/tpa-settings/react';
import {disableTranslationEscaping} from '../../../disableTranslationEscaping';
import {useLocaleKeys} from '../../../LocaleKeysProvider';
import {useSettingsWithDefaults} from '../../../../../hooks/useSettingsWithDefaults';
import {HeadingTags} from '../../../constants';

export enum HeaderDataHook {
  root = 'HeaderDataHook.root',
  title = 'HeaderDataHook.title',
  titleHtmlTag = 'HeaderDataHook.titleHtmlTag',
  subtitle = 'HeaderDataHook.subtitle',
  orderNumberLine = 'HeaderDataHook.orderNumberLine',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function Header() {
  const localeKeys = useLocaleKeys();
  const styleSettings = useStyles();
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const {buyerName, isSubscription, orderId, hideOrderNumber, useTitleHtmlTagSettingParam} =
    useControllerProps().thankYouPageStore;

  const getSubscriptionTitle = () => {
    return (
      getSettingsWithDefaults(settingsParams.THANK_YOU_PAGE_SUBSCRIPTION_TITLE) ??
      localeKeys.THANK_YOU_PAGE_SUBSCRIPTION_TITLE(disableTranslationEscaping({fullName: buyerName}))
    );
  };

  const getOrderTitleHtmlTag = () => {
    const titleHtmlTag = getSettingsWithDefaults(settingsParams.THANK_YOU_PAGE_TITLE_HTML_TAG) ?? HeadingTags.H1;
    const titleContent = (
      <span data-hook={HeaderDataHook.title}>{isSubscription ? getSubscriptionTitle() : getOrderTitle()}</span>
    );

    switch (titleHtmlTag) {
      case HeadingTags.P:
        return (
          <p data-hook={HeaderDataHook.titleHtmlTag} className={s.title}>
            {titleContent}
          </p>
        );
      case HeadingTags.H2:
        return (
          <h2 data-hook={HeaderDataHook.titleHtmlTag} className={s.title}>
            {titleContent}
          </h2>
        );
      case HeadingTags.H3:
        return (
          <h3 data-hook={HeaderDataHook.titleHtmlTag} className={s.title}>
            {titleContent}
          </h3>
        );
      case HeadingTags.H4:
        return (
          <h4 data-hook={HeaderDataHook.titleHtmlTag} className={s.title}>
            {titleContent}
          </h4>
        );
      case HeadingTags.H5:
        return (
          <h5 data-hook={HeaderDataHook.titleHtmlTag} className={s.title}>
            {titleContent}
          </h5>
        );
      case HeadingTags.H6:
        return (
          <h6 data-hook={HeaderDataHook.titleHtmlTag} className={s.title}>
            {titleContent}
          </h6>
        );
      case HeadingTags.H1:
      default:
        return (
          <h1 data-hook={HeaderDataHook.titleHtmlTag} className={s.title}>
            {titleContent}
          </h1>
        );
    }
  };

  const getOrderTitle = () => {
    const customTitle = getSettingsWithDefaults(settingsParams.THANK_YOU_PAGE_TITLE);

    if (customTitle) {
      return customTitle;
    }

    return buyerName
      ? localeKeys.thankYouPage.title.withName(disableTranslationEscaping({name: buyerName}))
      : localeKeys.thankYouPage.title.withoutName();
  };

  const getSubTitle = () => {
    const customSubTitle = isSubscription
      ? getSettingsWithDefaults(settingsParams.THANK_YOU_PAGE_SUBSCRIPTION_SUBTITLE)
      : getSettingsWithDefaults(settingsParams.THANK_YOU_PAGE_SUBTITLE);
    if (customSubTitle) {
      return customSubTitle;
    }
    return localeKeys.thankYouPage.subtitle.orderConfirmation();
  };

  const getOrderNumberTitle = () => {
    const customTitle = getSettingsWithDefaults(settingsParams.THANK_YOU_PAGE_ORDER_NUMBER_LABEL);
    if (customTitle) {
      return customTitle;
    }

    return localeKeys.thankYouPage.subtitle.orderNumber();
  };

  return (
    <div data-hook={HeaderDataHook.root} className={s.root}>
      {useTitleHtmlTagSettingParam ? (
        getOrderTitleHtmlTag()
      ) : (
        <h1 className={s.title}>
          <span data-hook={HeaderDataHook.title}>
            {isSubscription ? /* istanbul ignore next */ getSubscriptionTitle() : getOrderTitle()}
          </span>
        </h1>
      )}

      <div className={s.subtitle} data-hook={HeaderDataHook.subtitle}>
        <span>{getSubTitle()}</span>
      </div>

      {!hideOrderNumber && !isSubscription && styleSettings.get(stylesParams.THANK_YOU_PAGE_ORDER_NUMBER_SELECTION) && (
        <div className={s.orderNumberLine} data-hook={HeaderDataHook.orderNumberLine}>
          <span className={s.orderNumberTitle}>{getOrderNumberTitle()}</span>
          <span className={s.orderNumberValue}>{orderId}</span>
        </div>
      )}
    </div>
  );
}
